import Heading from '@/common/Heading';
import AlertBanner from '@/components/AlertBanner';
import Button, { ButtonType } from '@/components/Button';
import Input, { InputHeight, InputStyle } from '@/components/Input';
import Text from '@/components/Text';
import AuthService from '@/lib/Auth';
import { firebaseAuth } from '@/lib/Firebase';
import MiscUtils from '@/utils/MiscUtils';
import PhoneUtils from '@/utils/PhoneUtils';
import { useMutation } from '@tanstack/react-query';
import { RecaptchaVerifier } from 'firebase/auth';
import Image from 'next/image';
import React, { useEffect } from 'react';

interface IEnterPhoneProps {
  onNext: () => void;
  phoneNumber: string;
  onChangePhone: (phone: string) => void;
}

function EnterPhone({ onNext, phoneNumber, onChangePhone }: IEnterPhoneProps) {
  const mutation = useMutation(AuthService.sendLoginVerificationCode, {
    onSuccess: (data) => {
      console.log('SENT VERIFICATION CODE SUCCCESSFULLY');
    },
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    mutation.mutate({ phoneNumber });
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (mutation.error) {
      // Clears the error alert at the bottom if there is any
      mutation.reset();
    }
    
    const formattedPhone = PhoneUtils.formatIncompletePhone(e.target.value, 'US');
    onChangePhone(formattedPhone);
  };

  useEffect(() => {
    if (MiscUtils.isBrowser()) {
      // @ts-ignore
      window.recaptchaVerifier = new RecaptchaVerifier(
        'loginBtn',
        {
          size: 'invisible',
          callback: () => {
            // reCAPTCHA solved, allow signInWithPhoneNumber.
            onNext();
          },
        },
        firebaseAuth
      );
    }
  }, []);

  const btnDisabled = !phoneNumber;

  return (
    <div className="flex flex-col gap-6 justify-center items-center md:w-[480px] h-full">

      <Image src="/images/banners/signin_banner.png?v=1" width={480} height={480 * 0.66} alt='Welcome to Bloom Web' />
      <div className="bg-darkglass w-full rounded-xl text-center p-4">
        <div className="flex flex-col items-center mb-4">
          <Heading className="w-auto mx-auto font-game uppercase text-center text-title-1">CONTINUE WITH YOUR PHONE NUMBER</Heading>
          <Text size="base" className="text-center text-secondary">We’ll send you a one-time verification code</Text>
        </div>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Input
            inputStyle={InputStyle.Primary}
            inputHeight={InputHeight.Medium}
            value={phoneNumber}
            onChange={onChange}
            required
            placeholder="(555) 555-5555"
          />
          <Button
            loading={mutation.isLoading || mutation.isSuccess}
            type={ButtonType.Primary}
            size='md'
            buttonType="submit"
            className="w-full"
            disabled={btnDisabled}
            id="loginBtn"
          >
            {btnDisabled ? 'Signin' : 'Continue'}
          </Button>
        </form>
      </div>
      <>
        {mutation.error && (
          <AlertBanner
            type="error"
            message="We ran into an unexpected issue. Please try again later."
            className="mt-4"
          />
        )}
      </>
    </div>
  );
}

export default EnterPhone;
